import React, { useState, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { Check, AlertCircle, FileDown } from 'lucide-react';
import { format } from 'date-fns';
import { FormValues, projectTypes, budgetRangeOptions, timelineOptions } from '../FormSchema';

/**
 * Fourth and final step of the multi-step form - Proposal Preview
 */
export function ProposalPreviewStep() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const { watch, getValues } = useFormContext<FormValues>();
  const formData = watch();
  
  // Get label for project type
  const getProjectTypeLabel = (value: string) => {
    return projectTypes.find(type => type.value === value)?.label || value;
  };
  
  // Get label for timeline
  const getTimelineLabel = (value: string) => {
    return timelineOptions.find(option => option.value === value)?.label || value;
  };
  
  // Get label for budget range
  const getBudgetRangeLabel = (value: string) => {
    return budgetRangeOptions.find(option => option.value === value)?.label || value;
  };
  
  // Removed estimated delivery calculation
  
  // Removed step helper function
  
  const proposalRef = useRef<HTMLDivElement>(null);

  // Function to generate a clean, minimal PDF that matches the example design
  const generatePDF = async () => {
    // Set generating state to display loading UI
    setIsGenerating(true);
    
    // Create loading indicator
    const loadingIndicator = document.createElement('div');
    loadingIndicator.textContent = 'Generating PDF...';
    loadingIndicator.style.position = 'fixed';
    loadingIndicator.style.top = '50%';
    loadingIndicator.style.left = '50%';
    loadingIndicator.style.transform = 'translate(-50%, -50%)';
    loadingIndicator.style.padding = '10px 20px';
    loadingIndicator.style.backgroundColor = 'rgba(0,0,0,0.7)';
    loadingIndicator.style.color = 'white';
    loadingIndicator.style.borderRadius = '4px';
    loadingIndicator.style.zIndex = '9999';
    document.body.appendChild(loadingIndicator);
    
    try {
      // Get latest form data
      const formData = getValues();

      // Initialize PDF with A4 format
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - 2 * margin;
      
      // Define colors - using a very minimal color scheme
      const textColor: [number, number, number] = [35, 35, 35];     // Dark gray/black for text
      const accentColor: [number, number, number] = [255, 0, 128];  // Fuchsia for accent elements
      const dividerColor: [number, number, number] = [220, 220, 220]; // Light gray for dividers
      
      // Set initial y position
      let y = 30;
      
      // Layout spacing variables
      const sectionSpacing = 25; // Space between sections
      const lineSpacing = 8;     // Space between lines of text
      const featureHeight = 8;   // Space between feature items
      
      // Start content
      // ===== HEADER =====
      
      // Title on left
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(textColor[0], textColor[1], textColor[2]);
      pdf.setFontSize(22);
      pdf.text('PROJECT', margin, y);
      y += 12;
      pdf.text('PROPOSAL', margin, y);
      
      // Client name in fuchsia on right
      pdf.setTextColor(accentColor[0], accentColor[1], accentColor[2]);
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`${formData.full_name || 'Client'}'s Project`, pageWidth - margin, y - 12, { align: 'right' });
      
      // Date on right
      pdf.setTextColor(textColor[0], textColor[1], textColor[2]);
      pdf.setFontSize(12);
      pdf.text(format(new Date(), 'MM/dd/yyyy'), pageWidth - margin, y, { align: 'right' });
      
      // Divider line below header
      pdf.setDrawColor(dividerColor[0], dividerColor[1], dividerColor[2]);
      pdf.setLineWidth(0.3);
      pdf.line(margin, y + 10, pageWidth - margin, y + 10);
      
      y += 30;
      
      // CLIENT INFORMATION section
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(textColor[0], textColor[1], textColor[2]);
      pdf.setFontSize(15);
      pdf.text('CLIENT INFORMATION', margin, y);
      y += 12;
      
      // Divider under section header
      pdf.setDrawColor(dividerColor[0], dividerColor[1], dividerColor[2]);
      pdf.setLineWidth(0.3);
      pdf.line(margin, y - 8, pageWidth - margin, y - 8);
      
      // Client info in 2x2 grid
      const labelWidth = 30;
      const col1 = margin;
      const col2 = margin + labelWidth + 10;
      const col3 = margin + contentWidth/2;
      const col4 = col3 + labelWidth + 10;
      
      // Row 1: Project Type and Client Name
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      pdf.text('Type:', col1, y);
      pdf.setFont('helvetica', 'normal');
      pdf.text(getProjectTypeLabel(formData.project_type), col2, y);
      
      pdf.setFont('helvetica', 'bold');
      pdf.text('Client', col3, y);
      pdf.setFont('helvetica', 'normal');
      pdf.text(formData.full_name || 'Not specified', col4, y);
      
      // Row 2: Company and Email
      y += 10;
      pdf.setFont('helvetica', 'bold');
      pdf.text('Company:', col1, y);
      pdf.setFont('helvetica', 'normal');
      pdf.text(formData.company_name || 'Not specified', col2, y);
      
      pdf.setFont('helvetica', 'bold');
      pdf.text('Email:', col3, y);
      pdf.setFont('helvetica', 'normal');
      pdf.text(formData.email || 'Not specified', col4, y);
      
      // Move to next section
      y += sectionSpacing;
      
      // PROJECT DESCRIPTION section
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(textColor[0], textColor[1], textColor[2]);
      pdf.setFontSize(15);
      pdf.text('PROJECT DESCRIPTION', margin, y);
      y += 12;
      
      // Divider under section header
      pdf.setDrawColor(dividerColor[0], dividerColor[1], dividerColor[2]);
      pdf.setLineWidth(0.3);
      pdf.line(margin, y - 8, pageWidth - margin, y - 8);
      
      // Description text - plain text without background
      const descriptionText = formData.project_description || 'Not provided';
      // Limit description length to avoid overflow
      const maxDescLength = Math.min(descriptionText.length, 300);
      const truncatedDesc = descriptionText.length > 300 ? 
        descriptionText.substring(0, 297) + '...' : descriptionText;
      const descLines = pdf.splitTextToSize(truncatedDesc, contentWidth);
      
      // Simple dark text on white background
      pdf.setTextColor(textColor[0], textColor[1], textColor[2]);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(12);
      pdf.text(descLines, margin, y);
      
      const descHeight = descLines.length * 6;
      y += descHeight + 5;
      
      // Move to next section
      y += sectionSpacing;
      
      // For single page - no page breaks
      // Just add a little spacing
      if (y > pageHeight - 60) {
        // If we're close to the bottom, compress even more
        y = Math.min(y, pageHeight - 55);
      }
            // Features Section
      if (formData.features && formData.features.length > 0) {
        // Section header for features
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(textColor[0], textColor[1], textColor[2]);
        pdf.setFontSize(15);
        pdf.text('KEY FEATURES', margin, y);
        y += 12;
        
        // Divider under section header
        pdf.setDrawColor(dividerColor[0], dividerColor[1], dividerColor[2]);
        pdf.setLineWidth(0.3);
        pdf.line(margin, y - 8, pageWidth - margin, y - 8);
        
        // Multi-column layout for features
        pdf.setTextColor(textColor[0], textColor[1], textColor[2]);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(12);

        // Calculate parameters for 2-column layout
        const colWidth = (contentWidth / 2) - 5; // Width for each column with some spacing
        const col1X = margin;
        const col2X = margin + contentWidth / 2 + 5;
        let currentY = y;
        const maxYForFeatures = pageHeight - 60; // Reserve space at bottom
        
        // Split features into 2 columns
        for (let i = 0; i < formData.features.length; i++) {
          // Determine which column to place the feature
          const colX = i % 2 === 0 ? col1X : col2X;
          
          // If we're starting a new row on the left column
          if (i % 2 === 0 && i > 0) {
            currentY += featureHeight; // Move to next row
          }
          
          // Check if we need to add a page
          if (currentY > maxYForFeatures) {
            pdf.addPage();
            currentY = 30; // Reset Y position on new page
          }
          
          // Draw styled bullet
          pdf.setFillColor(accentColor[0], accentColor[1], accentColor[2]);
          pdf.circle(colX + 2, currentY - 1, 1, 'F');
          
          // Draw feature text with truncation if needed
          const maxTextWidth = colWidth - 10; // Account for bullet and margin
          const featureText = formData.features[i];
          
          // Calculate if text needs truncation based on width
          const textWidth = pdf.getStringUnitWidth(featureText) * 12 / pdf.internal.scaleFactor;
          const finalText = textWidth > maxTextWidth ? 
            pdf.splitTextToSize(featureText, maxTextWidth)[0] + '...' : 
            featureText;
          
          pdf.text(finalText, colX + 5, currentY);
        }
        
        // Update Y position after all features
        y = Math.ceil(formData.features.length / 2) * featureHeight + currentY;
        
        y += 10; // Add spacing after features section
      }
      
      // For single page - no page breaks
      // Just add a little spacing
      if (y > pageHeight - 60) {
        // If we're close to the bottom, compress even more
        y = Math.min(y, pageHeight - 55);
      }
      
      // Design References section (removed for minimal design)
      // if (formData.has_design && formData.design_references && formData.design_references.length > 0) {
      //   // Skip this section for the minimal design requested
      //   // Moving to next section
      //   
      //   // Skipping design references for the minimal design requested
      //   if (false) {
      //     const url = formData.design_references[i];
      //     const displayUrl = url.length > 70 ? url.substring(0, 67) + '...' : url;
      //     
      //     // Draw small bullet
      //     pdf.setFillColor(accentColor[0], accentColor[1], accentColor[2]);
      //     pdf.circle(margin + 2, y - 1, 1, 'F');
      //     
      //     // Draw URL
      //     pdf.setTextColor(80, 80, 100); // URL color
      //     pdf.text(displayUrl, margin + 5, y);
      //     y += 5; // Tight spacing
      //   }
      //   
      //   // Show count of hidden references if we truncated
      //   if (formData.design_references.length > maxRefs) {
      //     pdf.setTextColor(120, 120, 130);
      //     pdf.text(`+ ${formData.design_references.length - maxRefs} more references`, margin + 5, y);
      //     y += 5;
      //   }
      //   
      //   // Move to next section
      //   y += 5;
      // }
      
      // For single page - no page breaks
      // Just add a little spacing
      if (y > pageHeight - 70) {
        // If we're close to the bottom, compress even more
        y = Math.min(y, pageHeight - 65);
      }
      
      // SCHEDULE & BUDGET section
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(textColor[0], textColor[1], textColor[2]);
      pdf.setFontSize(15);
      pdf.text('SCHEDULE & BUDGET', margin, y);
      y += 12;
      
      // Divider under section header
      pdf.setDrawColor(dividerColor[0], dividerColor[1], dividerColor[2]);
      pdf.setLineWidth(0.3);
      pdf.line(margin, y - 8, pageWidth - margin, y - 8);
      
      // Two-column label/value layout
      const labelX = margin;
      const valueX = margin + 45;
      pdf.setFontSize(12);
      pdf.setTextColor(textColor[0], textColor[1], textColor[2]);
      y += 2;
      
      // Timeline
      pdf.setFont('helvetica', 'bold');
      pdf.text('Timeline:', labelX, y);
      pdf.setFont('helvetica', 'normal');
      pdf.text(getTimelineLabel(formData.timeline), valueX, y);
      y += 10;
      
      // Budget
      pdf.setFont('helvetica', 'bold');
      pdf.text('Budget:', labelX, y);
      pdf.setFont('helvetica', 'normal');
      pdf.text(getBudgetRangeLabel(formData.budget_range), valueX, y);
      y += 10;
      
      // Deadline
      if (formData.start_date) {
        pdf.setFont('helvetica', 'bold');
        pdf.text('Deadline:', labelX, y);
        pdf.setFont('helvetica', 'normal');
        pdf.text(format(formData.start_date, 'MM/dd/yyyy'), valueX, y);
        y += 10;
      }
      
      // No footer for the minimal design
      
      // Save the PDF with a formatted name
      pdf.save(`${formData.full_name || 'Project'}_Proposal.pdf`);
      document.body.removeChild(loadingIndicator);
    } catch (error) {
      console.error('PDF generation error:', error);
      alert('There was an error generating your PDF. Please try again: ' + (error instanceof Error ? error.message : String(error)));
    }
  };

  return (
    <div className="space-y-6">
      <div ref={proposalRef} className="border border-white/10 rounded-lg overflow-hidden">
        <div className="bg-gradient-to-r from-primary/20 to-purple-500/20 p-4 border-b border-white/10">
          <h3 className="text-xl font-bold text-white">Project Summary</h3>
          <p className="text-white/70 text-sm mt-1">
            Review your project details before submission
          </p>
        </div>
        
        <div className="p-5 space-y-6">
          {/* Client Information */}
          <div>
            <h4 className="text-white/70 uppercase text-xs font-medium mb-3">Client Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white/5 p-3 rounded-md">
                <span className="text-white/50 text-sm">Name</span>
                <p className="text-white font-medium">{formData.full_name}</p>
              </div>
              <div className="bg-white/5 p-3 rounded-md">
                <span className="text-white/50 text-sm">Email</span>
                <p className="text-white font-medium">{formData.email}</p>
              </div>
              {formData.company_name && (
                <div className="bg-white/5 p-3 rounded-md">
                  <span className="text-white/50 text-sm">Company</span>
                  <p className="text-white font-medium">{formData.company_name}</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Project Description */}
          <div className="mt-6">
            <h4 className="text-white/70 uppercase text-xs font-medium mb-3">Project Description</h4>
            <div className="bg-white/5 p-4 rounded-md border border-white/10">
              <p className="text-white/90 whitespace-pre-wrap">{formData.project_description}</p>
            </div>
          </div>
          
          {/* Project Type */}
          <div className="mt-6">
            <h4 className="text-white/70 uppercase text-xs font-medium mb-3">Project Type</h4>
            <div className="bg-white/5 p-3 rounded-md">
              <span className="text-white/50 text-sm">Project Type</span>
              <p className="text-white font-medium">{getProjectTypeLabel(formData.project_type)}</p>
            </div>
          </div>
          
          {/* Features List */}
          {formData.features && formData.features.length > 0 && (
            <div className="mt-6">
              <h4 className="text-white/70 uppercase text-xs font-medium mb-3">Key Features</h4>
              <div className="bg-white/5 p-4 rounded-md border border-white/10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {formData.features.map((feature) => (
                    <div key={feature} className="flex items-center">
                      <Check className="text-primary mr-2 flex-shrink-0 w-4 h-4" />
                      <span className="text-white/90 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {/* Design References */}
          {formData.has_design && formData.design_references && formData.design_references.length > 0 && (
            <div className="mt-6">
              <h4 className="text-white/70 uppercase text-xs font-medium mb-3">Design References</h4>
              <div className="bg-white/5 p-4 rounded-md border border-white/10">
                <div className="space-y-2">
                  {formData.design_references.map((url, idx) => (
                    <div key={idx} className="flex items-center">
                      <a 
                        href={url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-white/90 text-sm hover:text-fuchsia-300 break-all"
                      >
                        {url}
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {/* Timeline and Budget */}
          <div className="mt-6">
            <h4 className="text-white/70 uppercase text-xs font-medium mb-3">Schedule & Budget</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white/5 p-3 rounded-md">
                <span className="text-white/50 text-sm">Timeline</span>
                <p className="text-white font-medium">{getTimelineLabel(formData.timeline)}</p>
              </div>
              <div className="bg-white/5 p-3 rounded-md">
                <span className="text-white/50 text-sm">Budget Range</span>
                <p className="text-white font-medium">{getBudgetRangeLabel(formData.budget_range)}</p>
              </div>
              {formData.start_date && (
                <div className="bg-white/5 p-3 rounded-md">
                  <span className="text-white/50 text-sm">Preferred Start Date</span>
                  <p className="text-white font-medium">{format(formData.start_date, 'MMMM d, yyyy')}</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Disclaimer */}
          <div className="flex items-start space-x-3 bg-white/5 p-3 rounded-md text-sm mt-6">
            <AlertCircle className="text-white/50 flex-shrink-0 w-5 h-5 mt-0.5" />
            <p className="text-white/70">
              Please review all your information above before submitting.
            </p>
          </div>
        </div>
      </div>
      
      <div className="mt-6 pb-2 pt-4">
        <div className="flex justify-between items-center">
          <Button
            type="button"
            variant="secondary"
            onClick={generatePDF}
            className="bg-gradient-to-r from-indigo-600 to-blue-500 text-white border-none hover:from-indigo-700 hover:to-blue-600"
          >
            <FileDown className="mr-2 h-4 w-4" />
            Save as PDF
          </Button>
          
          <p className="text-white/70 text-center flex-1 mx-4">
            By submitting this request, our team will review your project details and get back to you within 2 business days with a detailed proposal.
          </p>
        </div>
      </div>
    </div>
  );
}
